import { useMemo, useState } from 'react'
import { invalidateCriteria, verifyCriteria } from '../../services/licensing'
import type {
  ImportResponse,
  LicenseCriteria,
  LicenseCriteriaStatus,
  Organisation,
} from '../../types'

export type CriteriaImportMode = 'none' | 'json' | 'manual'

interface CriteriaTabStateInput {
  criteria: LicenseCriteria[]
  organisations: Organisation[]
  onRefresh: () => Promise<void>
}

export interface CriteriaFilters {
  filterStatus: LicenseCriteriaStatus | ''
  setFilterStatus: (value: LicenseCriteriaStatus | '') => void
  filterOrg: string
  setFilterOrg: (value: string) => void
}

export interface CriteriaImportControls {
  showImportSection: boolean
  toggleShowImportSection: () => void
  importMode: CriteriaImportMode
  setImportMode: (mode: CriteriaImportMode) => void
  finishImport: (result?: ImportResponse) => Promise<void>
}

export interface CriteriaActions {
  verifyingId: string | null
  invalidatingId: string | null
  handleVerify: (id: string) => Promise<void>
  handleInvalidate: (id: string) => Promise<void>
}

export interface CriteriaTabState {
  filters: CriteriaFilters
  importControls: CriteriaImportControls
  actions: CriteriaActions
  filteredCriteria: LicenseCriteria[]
  getOrgName: (orgId: string) => string
}

function useFilters(): CriteriaFilters {
  const [filterStatus, setFilterStatus] = useState<LicenseCriteriaStatus | ''>('')
  const [filterOrg, setFilterOrg] = useState('')
  return { filterStatus, setFilterStatus, filterOrg, setFilterOrg }
}

function useImportControls(onRefresh: () => Promise<void>): CriteriaImportControls {
  const [showImportSection, setShowImportSection] = useState(true)
  const [importMode, setImportMode] = useState<CriteriaImportMode>('none')
  const finishImport = async (): Promise<void> => {
    setImportMode('none')
    await onRefresh()
  }
  return {
    showImportSection,
    toggleShowImportSection: () => {
      setShowImportSection((prev) => !prev)
    },
    importMode,
    setImportMode,
    finishImport,
  }
}

function useActions(onRefresh: () => Promise<void>): CriteriaActions {
  const [verifyingId, setVerifyingId] = useState<string | null>(null)
  const [invalidatingId, setInvalidatingId] = useState<string | null>(null)
  const handleVerify = async (id: string): Promise<void> => {
    setVerifyingId(id)
    try {
      await verifyCriteria(id)
      await onRefresh()
    } finally {
      setVerifyingId(null)
    }
  }
  const handleInvalidate = async (id: string): Promise<void> => {
    setInvalidatingId(id)
    try {
      await invalidateCriteria(id)
      await onRefresh()
    } finally {
      setInvalidatingId(null)
    }
  }
  return { verifyingId, invalidatingId, handleVerify, handleInvalidate }
}

export function useCriteriaTabState(input: CriteriaTabStateInput): CriteriaTabState {
  const { criteria, organisations, onRefresh } = input
  const filters = useFilters()
  const importControls = useImportControls(onRefresh)
  const actions = useActions(onRefresh)
  const getOrgName = (orgId: string): string =>
    organisations.find((org) => org.id === orgId)?.name ?? 'Unknown'
  const filteredCriteria = useMemo(
    () =>
      criteria.filter((entry) => {
        if (filters.filterStatus && entry.status !== filters.filterStatus) return false
        if (filters.filterOrg && entry.organisationId !== filters.filterOrg) return false
        return true
      }),
    [criteria, filters.filterStatus, filters.filterOrg]
  )
  return { filters, importControls, actions, filteredCriteria, getOrgName }
}
